import Vue from 'vue';
import { clone } from 'lodash';
import { createLogger } from 'lbs-pi-handy/src/general-purpose/logger';
import { IMAGE_ENHANCEMENTS, KEYS } from './consts';

const logger = createLogger('imageAdjustStore');

export function makeStore (uScope, settings, userConfig) {
  const store = new Vue({
    data () {
      return {
        // Used to track settings for each image. Note: Need to preserve state of pinned images else this could be simpler
        states: {},
        // Used to lookup current state
        selectedViewportId: null,
        // User preference to show or hide the settings button in the uScope toolbar
        showButton: true,
        // User presets
        presets: [],
        // The currently active preset
        selectedPreset: null
      };
    },

    computed: {
      currentState () {
        return this.states[this.selectedViewportId];
      },
      currentViewport () {
        return uScope.getViewport(this.selectedViewportId);
      }
    },

    methods: {
      refreshSettings (config) {
        this.presets.push(...config.presets);
        this.showButton = config.showButton;
      },

      handleViewportSelected (viewPort) {
        const id = viewPort.identifier;

        // If not present, create a new state handler for this viewport
        if (!this.states[id]) {
          this.$set(this.states, id, {
            brightness: IMAGE_ENHANCEMENTS.brightness.default,
            contrast: IMAGE_ENHANCEMENTS.contrast.default,
            gamma: IMAGE_ENHANCEMENTS.gamma.default
          });

          // When the viewport is destroyed remove its settings;
          // they should NOT persist if the vp is re-opened later
          viewPort.events.on('$destroy', () => delete this.states[id]);
        }

        this.selectedViewportId = id;
      },

      adjustImage (key) {
        if (this.currentViewport) { // Can be undefined when "closing" unpinned image
          this.currentViewport.adjustImage({ [key]: this.currentState[key] });
          this.selectedPreset = null;
        }
      },

      reset () {
        this.currentState.brightness = IMAGE_ENHANCEMENTS.brightness.default;
        this.currentState.contrast = IMAGE_ENHANCEMENTS.contrast.default;
        this.currentState.gamma = IMAGE_ENHANCEMENTS.gamma.default;
        this.selectedPreset = null;
      },

      findPreset (name) {
        return this.presets.find((preset) => preset.name === name);
      },

      applyPreset (preset) {
        if (preset === this.selectedPreset) {
          this.reset();
        } else if (preset) {
          Object.assign(this.currentState, preset.state);
          // The adjustment watchers will clear the preset, so, delay till after
          setTimeout(() => {
            this.selectedPreset = preset;
          }, 50);
        } else {
          logger.warn('Preset not specified');
        }
      },

      saveAsPreset (name = '', forceSave = false) {
        if (!name.trim()) {
          return;
        }

        const state = clone(this.currentState);
        let preset = this.findPreset(name);

        if (preset) { // update if existing
          if (!forceSave) {
            throw new Error('ALREADY_EXISTS');
          }
          preset.state = state;
        } else { // add if new
          preset = { name, state };
          this.presets.push(preset);
        }

        this.selectedPreset = preset;
        this.saveSettings();
      },

      deletePreset (name) {
        const preset = this.findPreset(name);
        if (preset) {
          this.presets.splice(this.presets.indexOf(preset), 1);
        } else {
          logger.warn('Preset not found', name);
        }
        this.saveSettings();
      },

      saveSettings () {
        settings.save(this.presets, this.showButton);
      }
    },

    watch: {
      'currentState.brightness' () {
        this.adjustImage('brightness');
      },
      'currentState.contrast' () {
        this.adjustImage('contrast');
      },
      'currentState.gamma' () {
        this.adjustImage('gamma');
      }
    },

    created () {
      userConfig.events.on(`change:${KEYS.SHOW_BUTTON}`, (showButton) => { // should settings manage this?
        this.showButton = showButton;
      });
    }
  });

  uScope.events.on('uscope:viewport:selected', store.handleViewportSelected);
  settings.events.on('load', store.refreshSettings);

  return store;
}
